import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Clock, UserCheck, Scale, ShieldCheck, Calculator } from "lucide-react";
import { useSeo } from "../components/site/Layout";
import { Section, CtaBand, Faq } from "../components/site/blocks";
import { Reveal } from "../components/site/Reveal";
import { ZoneMap } from "../components/site/ZoneMap";
import { IMAGES, SERVICES } from "../data/site";
import { METHOD_STEPS } from "./Methode";

const PILLS = [[Clock, "Réponse sous 48 h ouvrées"], [UserCheck, "Un interlocuteur unique"], [Scale, "Indépendant des entreprises"], [ShieldCheck, "Aides : estimations honnêtes"]];

const Hero = () => (
  <section data-testid="home-hero" className="roof-bg overflow-hidden">
    <div className="container-x grid lg:grid-cols-12 gap-10 pt-10 pb-16 md:pt-16 md:pb-24 items-center">
      <div className="lg:col-span-6">
        <p className="eyebrow rise">AMO & conseil habitat · Île-de-France</p>
        <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.04] rise rise-1" data-testid="home-tagline">
          Le logement adapté à <span className="text-brand-ink">chaque âge de la vie.</span>
        </h1>
        <p className="mt-6 text-lg sm:text-xl text-slate-600 font-light max-w-lg rise rise-2">Diagnostic, aides, coordination des travaux : nous rendons votre logement plus sûr et plus confortable.</p>
        <div className="mt-9 grid sm:grid-cols-2 gap-3 max-w-xl rise rise-3">
          <Link to="/particuliers" className="btn-accent justify-between whitespace-nowrap py-4" data-testid="hero-path-particulier">Je suis un particulier <ArrowRight className="h-5 w-5" aria-hidden="true" /></Link>
          <Link to="/professionnels" className="btn-primary justify-between whitespace-nowrap py-4" data-testid="hero-path-professionnel">Je suis un professionnel <ArrowRight className="h-5 w-5" aria-hidden="true" /></Link>
        </div>
        <Link to="/simulateur" className="mt-5 inline-flex items-center gap-2 font-display text-navy link-u rise rise-4" data-testid="hero-simulator-cta">Tester mon éligibilité aux aides <ArrowUpRight className="h-4 w-4" aria-hidden="true" /></Link>
      </div>
      <div className="lg:col-span-6 relative rise rise-2">
        <img src={IMAGES.hero} alt="Femme âgée souriante marchant dans le couloir lumineux et adapté de son appartement" className="w-full aspect-[4/3] object-cover rounded-[36px] shadow-[0_50px_100px_-50px_rgba(16,36,62,0.55)]" fetchPriority="high" />
        <div className="absolute -bottom-6 left-5 glass rounded-2xl px-5 py-4 flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl bg-brand-ink text-white flex items-center justify-center"><Calculator className="h-5 w-5" aria-hidden="true" /></span>
          <span><span className="block font-display text-navy text-[15px]">MaPrimeAdapt'</span><span className="block text-sm text-slate-600">jusqu'à 70 % des travaux*</span></span>
        </div>
      </div>
    </div>
    <div className="container-x pb-12">
      <ul className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="home-commitments">
        {PILLS.map(([Icon, t]) => <li key={t} className="flex items-center gap-3 rounded-2xl bg-white/80 border border-brand/15 px-4 py-3 text-[15px] text-navy"><Icon className="h-5 w-5 text-brand-ink shrink-0" aria-hidden="true" />{t}</li>)}
      </ul>
      <p className="mt-3 text-xs text-slate-600">* Montants indicatifs, sous réserve d'éligibilité et des règles en vigueur.</p>
    </div>
  </section>
);

const BENTO = ["lg:col-span-2 lg:row-span-2", "", "", "", "", "lg:col-span-2", "lg:col-span-2"];
const ServicesBento = () => (
  <Section eyebrow="Services" title="Ce que nous faisons pour vous" testid="home-services">
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[minmax(190px,auto)]">
      {SERVICES.map((s, i) => (
        <Reveal key={s.slug} delay={i * 60} className={BENTO[i]}>
          <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className={`group bento h-full flex flex-col p-6 ${i === 0 ? "bg-navy !border-navy" : ""}`}>
            {i === 0 && <img src={s.image} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-40 group-hover:opacity-50 group-hover:scale-[1.03] transition-[opacity,transform] duration-700" />}
            <span className={`relative h-11 w-11 rounded-2xl flex items-center justify-center ${i === 0 ? "bg-white/15 text-white backdrop-blur" : "bg-brand-100 text-brand-ink group-hover:bg-brand-ink group-hover:text-white transition-colors"}`}><s.icon className="h-5 w-5" aria-hidden="true" /></span>
            <div className="relative mt-auto pt-8">
              <h3 className={`text-xl ${i === 0 ? "!text-white text-2xl sm:text-3xl" : ""}`}>{s.title}</h3>
              <p className={`mt-1.5 text-[15px] ${i === 0 ? "text-white/80 max-w-sm" : "text-slate-600"}`}>{s.short}</p>
            </div>
            <ArrowUpRight className={`absolute right-5 top-5 h-5 w-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 ${i === 0 ? "text-white" : "text-brand-ink"}`} aria-hidden="true" />
          </Link>
        </Reveal>
      ))}
    </div>
  </Section>
);

const Method = () => (
  <Section tone="mist" eyebrow="Méthode" title="Quatre étapes, zéro surprise" testid="home-method">
    <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {METHOD_STEPS.map((s, i) => (
        <Reveal as="li" key={s.t} delay={i * 90} className="relative rounded-3xl bg-white border border-brand/15 p-6">
          <span className="font-display text-5xl text-brand-200">{String(i + 1).padStart(2, "0")}</span>
          <h3 className="mt-4 text-xl">{s.t}</h3>
          <p className="mt-2 text-[15px] text-slate-600">{s.d}</p>
        </Reveal>
      ))}
    </ol>
    <Link to="/notre-methode" className="mt-8 inline-flex items-center gap-2 font-display text-navy link-u" data-testid="home-method-link">Notre méthode en détail <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
  </Section>
);

const Aids = () => (
  <Section eyebrow="Aides" title="Votre projet peut être largement financé" testid="home-aids">
    <div className="grid lg:grid-cols-3 gap-4">
      {[["MaPrimeAdapt'", "50 à 70 % des travaux d'adaptation*"], ["MaPrimeRénov'", "Rénovation énergétique, selon revenus*"], ["CEE & aides locales", "Compléments possibles*"]].map(([t, d], i) => (
        <Reveal key={t} delay={i * 80}><Link to="/aides-financieres" className="group bento block p-6 h-full" data-testid={`home-aid-${i}`}><h3 className="text-xl">{t}</h3><p className="mt-2 text-slate-600 text-[15px]">{d}</p></Link></Reveal>
      ))}
      <Reveal className="lg:col-span-3">
        <div className="rounded-3xl bg-brand-ink text-white p-7 sm:p-9 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div><h3 className="text-2xl sm:text-3xl !text-white">Suis-je éligible ?</h3><p className="mt-2 text-white/80">3 questions, résultat indicatif immédiat (barèmes Anah 2026).</p></div>
          <Link to="/simulateur" className="btn bg-white text-navy hover:bg-brand-100" data-testid="home-simulator-cta">Lancer le simulateur <ArrowRight className="h-5 w-5" aria-hidden="true" /></Link>
        </div>
      </Reveal>
    </div>
    <p className="mt-4 text-sm text-slate-600">* Montants indicatifs, sous réserve d'éligibilité et des règles en vigueur.</p>
  </Section>
);

const FAQ_TEASER = [
  { q: "Réalisez-vous les travaux ?", a: "Non. Des entreprises tierces qualifiées et assurées les réalisent ; nous conseillons, coordonnons et contrôlons." },
  { q: "Comment obtenir un diagnostic ?", a: "Cliquez sur « Prendre RDV » et choisissez un créneau : un conseiller vous recontacte pour organiser la visite." },
  { q: "Les montants d'aides sont-ils garantis ?", a: "Non : ils sont indicatifs, sous réserve d'éligibilité et des règles en vigueur. Seul l'organisme instructeur décide." },
  { q: "Où intervenez-vous ?", a: "Dans toute l'Île-de-France, et sur demande dans les régions limitrophes." },
];

export default function Home() {
  useSeo("Le logement adapté à chaque âge de la vie", "Habitalys accompagne particuliers et professionnels : adaptation du logement, sécurisation, rénovation énergétique, aides MaPrimeAdapt' et MaPrimeRénov', coordination des travaux en Île-de-France.");
  return (
    <>
      <Hero />
      <ServicesBento />
      <Method />
      <Aids />
      <Section tone="mist" eyebrow="Zone d'intervention" title="Île-de-France et régions limitrophes" testid="home-zone"><Reveal><ZoneMap compact /></Reveal></Section>
      <Section eyebrow="FAQ" title="Questions fréquentes" testid="home-faq">
        <Faq items={FAQ_TEASER} prefix="home-faq" />
        <Link to="/faq" className="mt-6 inline-flex items-center gap-2 font-display text-navy link-u" data-testid="home-faq-link">Toutes les questions <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
      </Section>
      <CtaBand />
    </>
  );
}
